import React, { useCallback, useState, memo, useMemo } from 'react';
import { Input, Button, Table, Space, Tooltip, Popconfirm } from '../../../../components';
import { PlusOutlined, DeleteOutlined, EditOutlined } from '@ant-design/icons';
import './KeyValuePairs.scss';

const ErrorMessages = memo(({ messages }) => {
    if (messages?.length) {
        return <div className="error-message">{messages.join(', ')}</div>;
    }
    return null;
});

export const KeyValuePairs = ({
    dataSource = {},
    onChange,
    keyPlaceholder = 'key',
    valuePlaceholder = 'value',
    instructionMessage = 'Click the + icon or press Enter to add a pair.',
    limit = 50,
    disabled = false,
    allowEmptyValue = false,
    keyValidation = {},
    valueValidation = {},
}) => {
    const [currentKey, setCurrentKey] = useState('');
    const [currentValue, setCurrentValue] = useState('');
    const [errors, setErrors] = useState({});
    const [editingKey, setEditingKey] = useState(null);

    const keyValueTableData = useMemo(() => {
        return Object.entries(dataSource).map(([key, val]) => ({ key, value: val }));
    }, [dataSource]);

    const validateKey = (key, isEditing = false) => {
        const messages = [];
        if (keyValidation.maxLength && key.length > keyValidation.maxLength) {
            messages.push(`Key cannot exceed ${keyValidation.maxLength} characters`);
        }

        const isDuplicate = dataSource.hasOwnProperty(key);
        if (isDuplicate && (!isEditing || key !== editingKey)) {
            messages.push('Key already exists');
        }

        return messages;
    };

    const validateValue = (val) => {
        const messages = [];

        if (!allowEmptyValue) {
            if (!val?.trim()) {
                messages.push('Value cannot be empty');
            }

            if (valueValidation.maxLength && val.length > valueValidation.maxLength) {
                messages.push(`Value cannot exceed ${valueValidation.maxLength} characters`);
            }
        }

        return messages;
    };

    const handleAdd = () => {
        const trimmedKey = currentKey.trim();
        const trimmedValue = currentValue.trim();
        const keyErrors = validateKey(trimmedKey, !!editingKey);
        const valueErrors = validateValue(trimmedValue);

        if (keyErrors.length > 0 || valueErrors.length > 0) {
            setErrors({
                key: keyErrors,
                value: valueErrors,
            });
            return;
        }

        if (Object.keys(dataSource).length >= limit) {
            setErrors({
                general: [`Cannot add more than ${limit} key-value pairs`],
            });
            return;
        }

        const dataToSave = {
            ...dataSource,
            [trimmedKey]: trimmedValue,
        };

        onChange?.(dataToSave);
        setCurrentKey('');
        setCurrentValue('');
        setEditingKey(null);
        setErrors({});
    };

    const handleEdit = (keyToEdit) => {
        setCurrentKey(keyToEdit);
        setCurrentValue(dataSource[keyToEdit]);
        setEditingKey(keyToEdit);
        setErrors({});
    };

    const handleCancel = () => {
        setCurrentKey('');
        setCurrentValue('');
        setEditingKey(null);
        setErrors({});
    };

    const handleRemove = (keyToRemove) => {
        const newValue = { ...dataSource };
        delete newValue[keyToRemove];
        onChange?.(newValue);
    };

    const handleKeyDown = (e) => {
        if (e.key === 'Enter') {
            e.preventDefault();
            handleAdd();
        }
    };

    const columns = [
        {
            title: 'Key',
            dataIndex: 'key',
            key: 'key',
            width: '38%',
        },
        {
            title: 'Value',
            dataIndex: 'value',
            key: 'value',
            width: '38%',
        },
        {
            title: 'Action',
            key: 'action',
            width: '24%',
            align: 'center',
            render: (_, record) => (
                <Space size="small">
                    <Tooltip title="Edit">
                        <Button
                            type="link"
                            aria-label={`Edit ${record.key}`}
                            icon={<EditOutlined />}
                            onClick={() => handleEdit(record.key)}
                            disabled={disabled || !!editingKey}
                        />
                    </Tooltip>
                    <Popconfirm
                        okText="Yes"
                        cancelText="No"
                        title="Are you sure to delete this?"
                        onConfirm={() => handleRemove(record.key)}
                        okButtonProps={{ danger: true }}
                        disabled={disabled || !!editingKey}
                    >
                        <Tooltip title="Delete">
                            <Button
                                type="link"
                                danger
                                aria-label={`Delete ${record.key}`}
                                icon={<DeleteOutlined />}
                                disabled={disabled || !!editingKey}
                            />
                        </Tooltip>
                    </Popconfirm>
                </Space>
            ),
        },
    ];

    const disableAddButton = useCallback(() => {
        var btnDisabled = disabled;
        if (!currentKey.trim()) {
            btnDisabled = true;
        }
        if (!allowEmptyValue && !currentValue.trim()) {
            btnDisabled = true;
        }
        return btnDisabled;
    }, [currentKey, allowEmptyValue, currentValue, disabled]);

    return (
        <div className="key-value-pairs">
            {keyValueTableData.length > 0 && (
                <Table
                    dataSource={keyValueTableData}
                    columns={columns}
                    pagination={false}
                    size="small"
                    rowKey="key"
                    rowClassName={(record) => (record.key === editingKey ? 'key-value-pairs__row--editing' : '')}
                    bordered
                />
            )}

            <div className="key-value-pairs__inputs">
                <Input
                    className="key-value-pairs__input"
                    placeholder={keyPlaceholder}
                    value={currentKey}
                    onChange={(e) => {
                        setCurrentKey(e.target.value);
                        setErrors({});
                    }}
                    onKeyDown={handleKeyDown}
                    disabled={disabled}
                    status={errors.key?.length ? 'error' : undefined}
                />
                <Input
                    className="key-value-pairs__input"
                    placeholder={valuePlaceholder}
                    value={currentValue}
                    onChange={(e) => {
                        setCurrentValue(e.target.value);
                        setErrors({});
                    }}
                    onKeyDown={handleKeyDown}
                    disabled={disabled}
                    status={errors.value?.length ? 'error' : undefined}
                />
                <Button
                    type="primary"
                    icon={editingKey ? undefined : <PlusOutlined />}
                    onClick={handleAdd}
                    disabled={disableAddButton()}
                >
                    {editingKey ? 'Save' : 'Add'}
                </Button>
                {editingKey && (
                    <Button onClick={handleCancel} disabled={disabled}>
                        Cancel
                    </Button>
                )}
            </div>

            <ErrorMessages messages={errors.key} />
            <ErrorMessages messages={errors.value} />
            <ErrorMessages messages={errors.general} />

            {instructionMessage && <div className="instruction-message">{instructionMessage}</div>}
        </div>
    );
};
